import {Injectable} from '@angular/core';
import {HttpService} from '@core/backend/common/api/http.service';
import {Observable, of} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    APIResponse,
    Filter,
    FilterFieldOption,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    PagingResponse,
    PresignedURL,
} from '@core/interfaces/system/system-common';
import {DocumentFormScope, DocumentScope, DocumentUpdateDto, Form, FormScope} from '@core/interfaces/common/forms';
import {FormResponseDto} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';
import {Document, DocumentCreate} from '@core/interfaces/common/document';
import {HttpParams} from '@angular/common/http';
import {Dataset} from '@core/interfaces/common/dataset';
import {map} from 'rxjs/operators';

@Injectable()
export class FormsApi {
    private readonly apiController: string = 'mobile/forms';
    private readonly documentsPrefix: string = 'mobile/documents/inspection-documents';
    private readonly formDocumentsPrefix: string = 'mobile/documents/form-inspection-documents';

    constructor(private api: HttpService) {}

    // Form content: basic content
    public createForm(name: string, description: string): Observable<APIResponse<Form>> {
        return this.api.post(`${this.apiController}/`, {
            name,
            description,
        });
    }

    public toggleFormPublish(formId: number, isPublished: boolean): Observable<APIResponse<Form>> {
        return this.api.put(`${this.apiController}/toggle-publish/${formId}`, {
            published: isPublished,
        });
    }

    public updateForm(formId: number, name: string, description: string): Observable<APIResponse<Form>> {
        return this.api.put(`${this.apiController}/${formId}`, {
            name,
            description,
        });
    }

    public getInspectionFormDto(formId: number): Observable<APIResponse<FormResponseDto>> {
        return this.api.get(`${this.apiController}/${formId}`);
    }

    // Form content: scope
    public getFormScope(formId: number): Observable<APIResponse<Filter[]>> {
        return this.api.get(`${this.apiController}/scope/${formId}`);
    }

    public setFormScope(formId: number, filters: Filter[]): Observable<APIResponse<any>> {
        return this.api.post(`${this.apiController}/scope/${formId}`, {
            filters,
        });
    }

    public getFilterOptions(fieldKey: string, searchString: string): Observable<APIResponse<FilterFieldOption[]>> {
        return this.api.post(`${this.apiController}/filter-options`, {
            fieldKey,
            searchString,
        });
    }

    // Form content: config
    public commitFormConfig(
        formId: number,
        isConfirmed: boolean,
        dataPrepToolId: number,
    ): Observable<APIResponse<Form>> {
        return this.api.post(`${this.apiController}/config/commit/${formId}`, {
            dataPrepToolId: dataPrepToolId,
            isConfirmed: isConfirmed,
        });
    }

    public downloadFormConfig(formId: number): Observable<APIResponse<DownloadCSVUrl[]>> {
        return this.api.get(`${this.apiController}/dto/download-form/${formId}`);
    }

    // Other
    public getInspectionFormsDataSource(): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.apiController}/dto/list`);
    }
    public getSelectedInspectionFormsDataSource(selectedId: number[]): Observable<PagingResponse<FormScope>> {
        if (selectedId.length > 0) {
            const params = 'filterByfilterByid_IN=' + selectedId.join('|') + `&pageNumber=1&pageSize=1000`;
            return this.api.get(`${this.apiController}/dto/list?${params}`);
        } else {
            return of();
        }
    }
    public getInspectionForms(pageSize: number = 1000): Observable<Form[]> {
        return this.api.get(`${this.apiController}/dto/list?pageNumber=1&pageSize=${pageSize}`);
    }

    public duplicateForm(formId: number): Observable<APIResponse<any>> {
        return this.api.post(`${this.apiController}/duplicate/${formId}`, {});
    }

    public deleteForm(formId: number): Observable<APIResponse<any>> {
        return this.api.delete(`${this.apiController}/${formId}`);
    }

    public downloadInspectionForms(): Observable<APIResponse<DownloadCSVUrl>> {
        return this.api.get(`${this.apiController}/dto/list/csv`);
    }

    updateDocumentScopeByFormId(
        formIds: number,
        params: {inspectionDocumentIds: number[]},
    ): Observable<APIResponse<Document>> {
        return this.api.put(`${this.apiController}/${formIds}/inspection-documents`, params);
    }

    // Document management
    listFiles(): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.documentsPrefix}/dto/list`);
    }
    listFilesByFormId(formId: number): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.documentsPrefix}/${formId}/dto/list`);
    }
    listSelectedFiles(selectedId: number[]): Observable<PagingResponse<DocumentScope>> {
        if (selectedId.length > 0) {
            const params = 'filterByfilterByid_IN=' + selectedId.join('|') + `&pageNumber=1&pageSize=1000`;
            return this.api.get(`${this.documentsPrefix}/dto/list?${params}`);
        } else {
            return of();
        }
    }
    create(document: DocumentCreate): Observable<APIResponse<Document>> {
        return this.api.post(`${this.documentsPrefix}/`, document);
    }
    update(documentId: number, document: DocumentUpdateDto): Observable<APIResponse<Document>> {
        return this.api.put(`${this.documentsPrefix}/${documentId}`, document);
    }
    updateDocumentScope(documentId: number, params: {formIds: number[]}): Observable<APIResponse<Document>> {
        return this.api.put(`${this.documentsPrefix}/${documentId}/forms`, params);
    }

    deleteDocument(documentId: number): Observable<APIResponse<boolean>> {
        return this.api.delete(`${this.documentsPrefix}/${documentId}`);
    }
    getSelectedFormsByDocumentId(documentId: number): Observable<APIResponse<PagingResponse<DocumentFormScope>>> {
        return this.api.get(
            `${this.formDocumentsPrefix}?pageNumber=1&pageSize=10000&notDeletedForms=true&inspectionDocumentId=${documentId}`,
        );
    }
    getSelectedDocumentByFormsId(formId: number): Observable<APIResponse<PagingResponse<DocumentFormScope>>> {
        return this.api.get(
            `${this.formDocumentsPrefix}?pageNumber=1&pageSize=10000&notDeletedForms=true&formId=${formId}`,
        );
    }
    getFilterFieldOptions(req: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.documentsPrefix}/options/filters/field`, req);
    }
    getPresignedUrl(fileName: string, fileFormat: string): Observable<APIResponse<PresignedURL>> {
        return this.api.post(`${this.documentsPrefix}/presigned-url`, {fileName, fileFormat});
    }
    getDownloadUrl(documentId: number): Observable<any> {
        return this.api
            .get(`${this.documentsPrefix}/${documentId}/download-url`)
            .pipe(map((res: APIResponse<{url: string}>) => res.response.url));
    }
    upload(url: string, fileData: File): Observable<any> {
        return this.api.uploadS3(`${url}`, fileData, {
            reportProgress: true,
            observe: 'events',
            headers: {
                'Content-Disposition': 'attachment',
                'Content-Type': fileData.type,
            },
        });
    }
}
